import { AUTH_URL } from 'src/configs';
import request from 'src/request';

export type UserName = {
  first?: string;
  last?: string;
};

export type AdminPlatformUser = {
  id: string;
  email?: string;
  email_verified?: boolean;
  name?: UserName;
  phone_number?: { code?: string; number?: string };
  phone_number_verified?: boolean;
  twoFA_verified?: boolean;
  status?: string;
  kyc_status?: string;
  bxlend_id?: string;
  referred_by?: string | null;
  created_at?: string;
  updated_at?: string;
  country_code?: string;
  address?: unknown;
  [key: string]: unknown;
};

export type ListMeta = {
  page: number;
  limit: number;
  page_count: number;
  total_count: number;
  next_cursor?: string | null;
};

export type UserBalanceHolding = {
  currency_code: string;
  balance: string;
  rate?: number;
  value?: string;
};

export type UserSummary = {
  user: AdminPlatformUser;
  balance: {
    currency: string;
    total: string;
    holdings: UserBalanceHolding[];
    unpriced: Array<{ currency_code: string; balance: string } | string>;
  };
};

export type UserTransaction = {
  id: string;
  type?: string;
  kind?: string;
  status?: string;
  currency_code?: string;
  amount?: string;
  address?: string;
  direction?: string;
  created_at?: string;
  [key: string]: unknown;
};

export type UserDeposit = {
  id: string;
  kind?: string;
  status?: string;
  currency_code?: string;
  amount?: string;
  deposit_to?: string;
  deposit_to_address?: string | null;
  description?: string;
  attachments?: string[];
  is_executed?: boolean;
  created_at?: string;
  [key: string]: unknown;
};

export type UserDepositSummary = {
  currency: string;
  months: number;
  total: string;
  count: number;
  series: Array<{ month: string; value: string; count: number }>;
  unpriced?: string[];
};

export type UserWalletRow = {
  blockchain?: {
    id?: string;
    name?: string;
    symbol?: string;
    native_currency?: string;
    protocol_type?: string;
  };
  currency_code?: string;
  status?: string;
  wallet_id?: string | null;
  address?: string | null;
  balance?: string | null;
  activated_at?: string | null;
  last_deposit_at?: string | null;
  [key: string]: unknown;
};

export type UserKycRecord = {
  id?: string;
  _id?: string;
  name?: UserName | string;
  country_code?: string;
  address?: { full_address?: string; city?: string; pin_code?: string };
  user?: { id?: string } | string;
  status?: string;
  identification_type?: string;
  identification_url?: { front?: string; back?: string };
  photo_url?: string;
  response_message?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
};

export const USER_STATUSES = ['ACTIVE', 'INACTIVE', 'SUSPENDED', 'BANNED'] as const;

export const formatUserDisplayName = (user?: AdminPlatformUser | null) => {
  const first = user?.name?.first?.trim() || '';
  const last = user?.name?.last?.trim() || '';
  const full = `${first} ${last}`.trim();
  return full || user?.email || user?.id || 'User';
};

export const formatUserStatusLabel = (status?: string) => {
  if (!status) return '—';
  const normalized = status.toUpperCase().replace(/_/g, ' ');
  if (normalized === 'SUSPENDED') return 'Suspicious';
  return normalized.toLowerCase().replace(/(^|\s)\w/g, (c) => c.toUpperCase());
};

/** Title-case label for KYC / status chips matching design copy where known. */
export const formatKycStatusLabel = (status?: string) => {
  if (!status) return '—';
  const normalized = status.toUpperCase().replace(/_/g, ' ');
  if (normalized === 'VERIFIED') return 'Verified';
  if (normalized === 'UNVERIFIED') return 'Not Verified';
  if (normalized === 'PENDING') return 'Pending';
  if (normalized === 'CANCELED' || normalized === 'CANCELLED') return 'Canceled';
  if (normalized === 'FAILED') return 'Failed';
  if (normalized === 'EXPIRED') return 'Expired';
  return normalized.charAt(0) + normalized.slice(1).toLowerCase();
};

const buildQuery = (params: Record<string, string | number | undefined | null>) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    qs.set(key, String(value));
  });
  const str = qs.toString();
  return str ? `?${str}` : '';
};

export async function fetchPlatformUsers(params: {
  page?: number;
  limit?: number;
  sort?: string;
  order?: string;
  field?: string;
  value?: string;
}) {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/users${buildQuery({
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      sort: params.sort,
      order: params.order,
      field: params.field,
      value: params.value,
    })}`,
  );

  return {
    users: (response.data.users ?? []) as AdminPlatformUser[],
    meta: (response.data.meta ?? {
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      page_count: 1,
      total_count: 0,
    }) as ListMeta,
  };
}

export async function fetchPlatformUser(userId: string) {
  const response = await request.get(`${AUTH_URL}/v1/admin/users/${userId}`);
  return (response.data.user ?? response.data) as AdminPlatformUser;
}

export async function fetchPlatformUserSummary(userId: string, currency = 'USD') {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/users/${userId}/summary${buildQuery({ currency })}`,
  );
  return response.data as UserSummary;
}

export async function updatePlatformUserStatus(userId: string, status: string) {
  const response = await request.patch(`${AUTH_URL}/v1/admin/users/${userId}`, { status });
  return (response.data.user ?? response.data) as AdminPlatformUser;
}

export async function fetchPlatformUserTransactions(
  userId: string,
  params: {
    page?: number;
    limit?: number;
    sort?: string;
    order?: string;
    status?: string;
    kind?: string;
    field?: string;
    value?: string;
  } = {},
) {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/users/${userId}/transactions${buildQuery({
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      sort: params.sort,
      order: params.order,
      status: params.status,
      kind: params.kind,
      field: params.field,
      value: params.value,
    })}`,
  );

  return {
    transactions: (response.data.transactions ?? []) as UserTransaction[],
    meta: (response.data.meta ?? {
      page: 1,
      limit: 10,
      page_count: 1,
      total_count: 0,
    }) as ListMeta,
  };
}

export async function fetchPlatformUserDeposits(
  userId: string,
  params: {
    page?: number;
    limit?: number;
    sort?: string;
    order?: string;
    kind?: string;
    status?: string;
    currency_code?: string;
  } = {},
) {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/users/${userId}/deposits${buildQuery({
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      sort: params.sort,
      order: params.order,
      kind: params.kind,
      status: params.status,
      currency_code: params.currency_code,
    })}`,
  );

  return {
    deposits: (response.data.deposits ?? []) as UserDeposit[],
    meta: (response.data.meta ?? {
      page: 1,
      limit: 10,
      page_count: 1,
      total_count: 0,
    }) as ListMeta,
  };
}

export async function fetchPlatformUserDepositsSummary(
  userId: string,
  params: { months?: number; currency?: string; currency_code?: string } = {},
) {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/users/${userId}/deposits/summary${buildQuery({
      months: params.months ?? 12,
      currency: params.currency ?? 'USD',
      currency_code: params.currency_code,
    })}`,
  );
  return response.data as UserDepositSummary;
}

export async function fetchPlatformUserWallets(userId: string, status?: string) {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/users/${userId}/wallets${buildQuery({ status })}`,
  );

  return {
    wallets: (response.data.wallets ?? []) as UserWalletRow[],
    meta: (response.data.meta ?? { active: 0, total: 0 }) as { active: number; total: number },
  };
}

export async function fetchPlatformUserKyc(userId: string, page = 1, limit = 10) {
  const response = await request.get(
    `${AUTH_URL}/v1/admin/kyc${buildQuery({
      page,
      limit,
      field: 'user',
      value: userId,
    })}`,
  );

  const records = (response.data.kyc ?? response.data.data ?? []) as UserKycRecord[];
  return {
    kyc: records,
    meta: (response.data.meta ?? {
      page,
      limit,
      page_count: 1,
      total_count: records.length,
    }) as ListMeta,
  };
}

export async function updatePlatformUserKyc(
  kycId: string,
  payload: { status: string; response_message?: string },
) {
  const response = await request.patch(`${AUTH_URL}/v1/admin/kyc/${kycId}`, payload);
  return response.data;
}

/* ------------------------- Referrals ------------------------- */

/**
 * One row per referred user, with the referrer resolved from the bxlend_id the
 * referral was recorded under. "N/A" is the API's own placeholder for a referrer
 * that no longer resolves.
 */
export type ReferralRecord = {
  referral_id?: string;
  referral_name?: UserName | string | null;
  referral_bxlend_id?: string;
  referred_by_id?: string;
  referred_by_name?: UserName | string | null;
  referred_by_bxlend_id?: string;
};

export const formatReferralName = (name?: UserName | string | null) => {
  if (!name) return '—';
  if (typeof name === 'string') return name === 'N/A' ? '—' : name;
  const joined = [name.first, name.last].filter(Boolean).join(' ').trim();
  return joined || '—';
};

/** The API's placeholder for a missing id, which should not become a link. */
export const resolveReferralId = (id?: string) => (!id || id === 'N/A' ? undefined : id);

export const fetchReferrals = async (params: { page?: number; limit?: number }) => {
  const response = await request.get(`${AUTH_URL}/v1/admin/referral${buildQuery(params)}`);
  return {
    referrals: (response.data.data || []) as ReferralRecord[],
    meta: response.data.meta as ListMeta,
  };
};
